import { useField } from 'formik';
import { FormField, Input, Tooltip } from '@neshane/blueberry';

/**
 * Formik-wrapped Input Component
 * @param {React.ReactNode} tooltip - tooltip shown on hover of the input itself (wraps Input in Tooltip)
 * @param {React.ReactNode} infoTipContent - tooltip shown on (i) icon next to label (uses FormField infoTipContent)
 * @param {string} tooltipPlacement - placement for tooltip (top, bottom, left, right, etc.)
 */
export const FormikInput = ({
    label,
    helpText,
    className = '',
    required,
    id,
    disabled,
    readOnly,
    maxLength,
    placeholder,
    type = 'text',
    tooltip,
    infoTipContent,
    tooltipPlacement = 'top',
    ...props
}) => {
    const [field, meta] = useField(props);
    const hasError = meta.touched && meta.error;
    const inputId = id || props.name;

    const inputElement = (
        <Input
            id={inputId}
            fit="parent"
            type={type}
            maxLength={maxLength}
            placeholder={placeholder}
            isDisabled={disabled}
            isReadOnly={readOnly}
            isInvalid={hasError}
            {...field}
        />
    );

    // Decide tooltip strategy:
    // - infoTipContent => shows (i) icon next to label (FormField native)
    // - tooltip => wraps Input itself (hover input to see tooltip)
    // If both provided, infoTipContent takes precedence for label, tooltip wraps input
    const formFieldInfoTip = infoTipContent || undefined;

    return (
        <FormField
            className={className}
            label={required ? `${label} *` : label}
            htmlFor={inputId}
            description={helpText && !hasError ? helpText : undefined}
            alertMessage={hasError ? meta.error : undefined}
            infoTipContent={formFieldInfoTip}
        >
            {tooltip ? (
                <Tooltip content={tooltip} placement={tooltipPlacement}>
                    <div style={{ width: '100%' }}>{inputElement}</div>
                </Tooltip>
            ) : (
                inputElement
            )}
        </FormField>
    );
};

export default FormikInput;
